import type {
  BlockSummary,
  HarborEvent,
  HarborSnapshot,
  MempoolStats,
  MempoolTx,
  Network,
  TxDetail,
} from "./types";

export type {
  BlockSummary,
  HarborEvent,
  HarborSnapshot,
  MempoolStats,
  MempoolTx,
  Network,
  TxDetail,
};

const RELAY_HTTP = import.meta.env.VITE_RELAY_HTTP ?? "/harbor";
const RELAY_WS =
  import.meta.env.VITE_RELAY_WS ??
  `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/harbor/ws`;

export class HarborClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<(e: HarborEvent) => void>();
  private reconnectTimer: number | null = null;
  private intentionalClose = false;

  on(fn: (e: HarborEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: HarborEvent): void {
    for (const fn of this.listeners) fn(e);
  }

  async fetchSnapshot(network: Network = "testnet"): Promise<HarborSnapshot> {
    const res = await fetch(`${RELAY_HTTP}/snapshot?network=${network}`);
    if (!res.ok) throw new Error(`snapshot ${res.status}`);
    return (await res.json()) as HarborSnapshot;
  }

  /** Explorer REST tx detail via relay (CORS-safe). Returns null on miss/error. */
  async fetchTx(
    txid: string,
    network: Network = "testnet",
  ): Promise<TxDetail | null> {
    const id = encodeURIComponent(txid.trim());
    if (!id) return null;
    try {
      const res = await fetch(
        `${RELAY_HTTP}/tx/${id}?network=${network}`,
      );
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`tx ${res.status}`);
      return (await res.json()) as TxDetail;
    } catch {
      return null;
    }
  }

  connect(network: Network = "testnet"): void {
    this.intentionalClose = false;
    this.open(network);
  }

  disconnect(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer != null) window.clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  private open(network: Network): void {
    const url = `${RELAY_WS}?network=${network}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.emit({ type: "status", data: { connected: true } });
      ws.send(JSON.stringify({ method: "subscribe" }));
    };

    ws.onmessage = (msg) => {
      try {
        const parsed = JSON.parse(String(msg.data)) as HarborEvent;
        this.emit(parsed);
      } catch {
        // ignore malformed
      }
    };

    ws.onclose = () => {
      this.emit({
        type: "status",
        data: { connected: false, detail: "reconnect" },
      });
      if (!this.intentionalClose) {
        this.reconnectTimer = window.setTimeout(() => this.open(network), 2000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }
}
